import { useCallback, useEffect, useRef, type Ref } from "react";
import type { View } from "react-native";

import { setUniversalFocus } from "./accessibilityFocus";

/**
 * Merges a consumer-provided `buttonRef` with an internal ref and, when
 * `shouldAutoFocus` is true, moves screen-reader + keyboard focus to the node
 * once on mount. Shared by the review-card action buttons (EditButton,
 * RestoreButton); structured so DeleteButton can adopt it if it ever needs
 * focus management.
 *
 * @returns a ref callback to attach to the focusable element.
 */
export function useMergedFocusableRef(
  buttonRef: Ref<View> | undefined,
  shouldAutoFocus: boolean,
): (node: View | null) => void {
  const internalRef = useRef<View>(null);

  const setRefs = useCallback(
    (node: View | null) => {
      internalRef.current = node;
      if (typeof buttonRef === "function") {
        buttonRef(node);
      } else if (buttonRef) {
        buttonRef.current = node;
      }
    },
    [buttonRef],
  );

  useEffect(() => {
    if (shouldAutoFocus) {
      setUniversalFocus(internalRef, { keyboardFocus: true });
    }
  }, [shouldAutoFocus]);

  return setRefs;
}
